import type { StairBlockNode, StairBlockNodeTable } from "building-types/stairs/stairTypes";
import { StairBlockType } from "building-types/stairs/stairTypesEnum";
import type { LevelInches } from "common/types/nominalNumbersTypes";
import { levelInchesNew } from "common/utils/nominalNumbers";

interface BlockInfo {
  type: StairBlockType;
  bottomRiser: number;
  topRiser: number;
  bottom?: LevelInches;
  top?: LevelInches;
}

const ReasonableMaxInt = Number.MAX_SAFE_INTEGER;
type BlockHeightTable = { [id: number]: BlockInfo };

export function stairBlockRiserTableNew(
  stairBlockNodeTable: StairBlockNodeTable,
  defaultRiserHeight: number,
): BlockHeightTable {
  const riserTwoThirdsHeight = defaultRiserHeight * (2 / 3);
  const blockRiserTable: BlockHeightTable = {};

  if (Object.keys(stairBlockNodeTable).length === 0) {
    return {};
  }

  for (const stairBlockId in stairBlockNodeTable) {
    let mostStringentRiserCount = ReasonableMaxInt;

    mostStringentRiserCount = resolveToLeastHeightAboveNodeRecursively(
      stairBlockNodeTable,
      +stairBlockId,
      {},
      blockRiserTable,
      mostStringentRiserCount,
    );

    const riserInfo = blockRiserTable[stairBlockId];
    blockRiserTable[stairBlockId] = { ...riserInfo, topRiser: mostStringentRiserCount };
  }

  for (const stairBlockId in stairBlockNodeTable) {
    resolveToLeastTopHeightBelowNode(stairBlockNodeTable, +stairBlockId, blockRiserTable);
  }

  for (const stairBlockId in stairBlockNodeTable) {
    let leastNeighborAboveRiser = ReasonableMaxInt;
    leastNeighborAboveRiser = resolveToLeastBottomHeightAboveNodeRecursively(
      stairBlockNodeTable,
      +stairBlockId,
      {},
      blockRiserTable,
      leastNeighborAboveRiser,
    );
    if (leastNeighborAboveRiser === ReasonableMaxInt) {
      continue;
    }
    const riserInfo = blockRiserTable[stairBlockId];
    blockRiserTable[stairBlockId] = { ...riserInfo, topRiser: leastNeighborAboveRiser };
  }

  const datums = makeDatumsOnIncrements(stairBlockNodeTable, blockRiserTable);
  snapBlockHeightsToDatums(stairBlockNodeTable, blockRiserTable, defaultRiserHeight, riserTwoThirdsHeight, datums);

  return blockRiserTable;
}

function resolveToLeastHeightAboveNodeRecursively(
  stairBlockNodeTable: StairBlockNodeTable,
  index: number,
  visited: { [id: number]: boolean } = {},
  blockRiserTable: BlockHeightTable,
  mostStringentRiserCount: number,
): number {
  if (visited[index]) {
    return mostStringentRiserCount;
  }
  visited[index] = true;
  const node = stairBlockNodeTable[index];

  if (node.topHeight !== undefined && node.topRiser !== undefined && node.topRiser < mostStringentRiserCount) {
    mostStringentRiserCount = node.topRiser;
  }

  for (const neighborAboveIndex of node.neighborsAbove) {
    const aboveMostStringentRiserCount = resolveToLeastHeightAboveNodeRecursively(
      stairBlockNodeTable,
      neighborAboveIndex,
      visited,
      blockRiserTable,
      mostStringentRiserCount,
    );
    if (aboveMostStringentRiserCount < mostStringentRiserCount) {
      mostStringentRiserCount = aboveMostStringentRiserCount;
    }
  }

  return mostStringentRiserCount;
}

function resolveToLeastTopHeightBelowNode(
  stairBlockNodeTable: StairBlockNodeTable,
  index: number,
  blockRiserTable: BlockHeightTable,
) {
  const node = stairBlockNodeTable[index] as StairBlockNode;
  let leastNeighborBelow = node.neighborsBelow.length === 0 ? 0 : ReasonableMaxInt;

  let hasLandingNeighborBelow = false;
  for (const childNodeIndex of node.neighborsBelow) {
    const childNode = stairBlockNodeTable[childNodeIndex];
    if (childNode.type === StairBlockType.RUN) {
      const riserInfo = blockRiserTable[childNodeIndex];
      if (riserInfo) {
        const minTopRiser = Math.min(riserInfo.bottomRiser + childNode.riserCount, riserInfo.topRiser);
        if (leastNeighborBelow > minTopRiser) {
          leastNeighborBelow = minTopRiser;
        }
      }
    } else {
      hasLandingNeighborBelow = true;
      const riserInfo = blockRiserTable[childNodeIndex];
      const { topRiser } = riserInfo;
      if (leastNeighborBelow > topRiser) {
        leastNeighborBelow = topRiser;
      }
    }
  }
  if (leastNeighborBelow === ReasonableMaxInt) {
    leastNeighborBelow = 0;
  }

  const riserInfo = blockRiserTable[index];

  if (node.type === StairBlockType.RUN) {
    const startRiser = node.bottomRiser !== undefined ? node.bottomRiser : leastNeighborBelow;
    blockRiserTable[index] = { ...riserInfo, bottomRiser: startRiser };
  } else {
    let stepUpLimit = leastNeighborBelow + 1;
    if (node.topRiser !== undefined && node.topRiser < stepUpLimit) {
      stepUpLimit = node.topRiser;
    }
    if (hasLandingNeighborBelow && node.stepUpFromPreviousLanding) {
      blockRiserTable[index] = { ...riserInfo, bottomRiser: leastNeighborBelow, topRiser: stepUpLimit };
    } else {
      blockRiserTable[index] = { ...riserInfo, bottomRiser: leastNeighborBelow, topRiser: leastNeighborBelow };
    }
  }
}

function resolveToLeastBottomHeightAboveNodeRecursively(
  stairBlockNodeTable: StairBlockNodeTable,
  index: number,
  visited: { [id: number]: boolean } = {},
  blockRiserTable: BlockHeightTable,
  leastNeighborAboveRiser: number,
) {
  if (visited[index]) {
    return leastNeighborAboveRiser;
  }
  visited[index] = true;

  const node = stairBlockNodeTable[index];

  if (node.neighborsAbove.length === 0) {
    return leastNeighborAboveRiser;
  }

  for (const childNodeIndex of node.neighborsAbove) {
    const childNode = stairBlockNodeTable[childNodeIndex];
    const riserInfo = blockRiserTable[childNodeIndex];

    if (childNode.type === StairBlockType.RUN) {
      const bottomRiser = riserInfo.bottomRiser;
      if (leastNeighborAboveRiser > bottomRiser) {
        leastNeighborAboveRiser = bottomRiser;
      }
    } else {
      //StairBlockType.LANDING
      const bottomRiser = riserInfo.bottomRiser;

      //node here refers to block below the childNode
      if (node.type !== StairBlockType.RUN) {
        if (leastNeighborAboveRiser > bottomRiser) {
          leastNeighborAboveRiser = bottomRiser;
        }
        const least = resolveToLeastBottomHeightAboveNodeRecursively(
          stairBlockNodeTable,
          childNodeIndex,
          visited,
          blockRiserTable,
          leastNeighborAboveRiser,
        );
        if (leastNeighborAboveRiser > least) {
          leastNeighborAboveRiser = least;
        }
      } else {
        const topRiser = riserInfo.topRiser;
        if (leastNeighborAboveRiser > topRiser) {
          leastNeighborAboveRiser = topRiser;
        }
        const least = resolveToLeastBottomHeightAboveNodeRecursively(
          stairBlockNodeTable,
          childNodeIndex,
          visited,
          blockRiserTable,
          leastNeighborAboveRiser,
        );
        if (leastNeighborAboveRiser > least) {
          leastNeighborAboveRiser = least;
        }
      }
    }
  }

  return leastNeighborAboveRiser;
}

function makeDatumsOnIncrements(
  stairBlockNodeTable: StairBlockNodeTable,
  blockRiserTable: BlockHeightTable,
  incrementsPerInch = 8,
) {
  const datums: { [eighthsOfAnInch: number]: boolean } = {};

  for (const stairBlockId in stairBlockNodeTable) {
    const node = stairBlockNodeTable[stairBlockId];
    let riserInfo = blockRiserTable[stairBlockId];

    const hasNeighborsBelow = node.neighborsBelow.length > 0;
    if (node.type === StairBlockType.RUN) {
      if (node.bottomHeight !== undefined) {
        riserInfo = { ...riserInfo, bottom: node.bottomHeight };
        setDatumValue(node.bottomHeight, datums, incrementsPerInch);
      } else if (!hasNeighborsBelow) {
        riserInfo = { ...riserInfo, bottom: levelInchesNew(0) };
        setDatumValue(0, datums, incrementsPerInch);
      }

      if (node.topHeight !== undefined) {
        riserInfo = { ...riserInfo, top: node.topHeight };
        setDatumValue(node.topHeight, datums, incrementsPerInch);
      }
    } else {
      //StairBlockType.LANDING

      if (node.topHeight !== undefined) {
        riserInfo = { ...riserInfo, top: node.topHeight };
        setDatumValue(node.topHeight, datums, incrementsPerInch);
      } else {
        if (!hasNeighborsBelow) {
          riserInfo = { ...riserInfo, top: levelInchesNew(0) };
          setDatumValue(0, datums, incrementsPerInch);
        }
      }
    }
    blockRiserTable[stairBlockId] = { type: node.type, ...(riserInfo as Partial<BlockInfo>) } as BlockInfo;
  }

  return Object.keys(datums).map((x) => +x / incrementsPerInch);
}

function snapBlockHeightsToDatums(
  stairBlockNodeTable: StairBlockNodeTable,
  blockRiserTable: BlockHeightTable,
  defaultRiserHeight: number,
  riserTwoThirdsHeight: number,
  datums: number[],
) {
  for (const stairBlockId in stairBlockNodeTable) {
    const node = stairBlockNodeTable[stairBlockId];
    let riserInfo = blockRiserTable[stairBlockId];
    const { type } = node;
    if (type === StairBlockType.RUN) {
      if (riserInfo.bottom === undefined) {
        riserInfo = {
          ...riserInfo,
          bottom: levelInchesNew(
            takeClosestValueIfWithinTolerance(riserInfo.bottomRiser * defaultRiserHeight, riserTwoThirdsHeight, datums),
          ),
        };
      }

      if (riserInfo.top === undefined) {
        riserInfo = {
          ...riserInfo,
          top: levelInchesNew(
            takeClosestValueIfWithinTolerance(riserInfo.topRiser * defaultRiserHeight, riserTwoThirdsHeight, datums),
          ),
        };
      }
    } else {
      //StairBlockType.LANDING
      if (riserInfo.top === undefined) {
        riserInfo = {
          ...riserInfo,
          top: levelInchesNew(
            takeClosestValueIfWithinTolerance(riserInfo.topRiser * defaultRiserHeight, riserTwoThirdsHeight, datums),
          ),
        };
      }
    }
    blockRiserTable[stairBlockId] = { type, ...(riserInfo as Partial<BlockInfo>) } as BlockInfo;
  }
}

function setDatumValue(value: number, datums: { [eighthsOfAnInch: number]: boolean }, increments: number) {
  const eighthsOfAnInch = Math.round(value * increments);
  datums[eighthsOfAnInch] = true;
}

function takeClosestValueIfWithinTolerance(height: number, tolerance: number, datums: number[]): number {
  const closestDatum = getClosestValueBinSearch(datums, height);
  if (Math.abs(closestDatum - height) <= tolerance) {
    return closestDatum;
  } else {
    return height;
  }
}

function getClosestValueBinSearch(arr: number[], x: number) {
  let low = -1;
  let high = arr.length;

  while (high - low > 1) {
    const mid = Math.round((low + high) / 2);
    if (arr[mid] <= x) {
      low = mid;
    } else {
      high = mid;
    }
  }
  if (arr[low] === x) {
    high = low;
  }

  const arrHigh = arr[high];
  const arrLow = arr[low];

  return Math.abs(arrHigh - x) <= Math.abs(arrLow - x) ? arrHigh : arrLow;
}
